import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { reportsApi } from '../../../api/client';
import RangeBar from './components/RangeBar';
import ReportTable from './components/ReportTable';
import useRangeState from './components/useRangeState';
import FiltersBar from './components/FiltersBar';
import ExportButtons from './components/ExportButtons';
import HelpButton from '../../help/HelpButton';

export default function AcdHistorical() {
  const [range, setRange, apiParams] = useRangeState('last_7');
  const [filters, setFilters] = useState({});
  const merged = { ...apiParams, ...filters };

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['reports', 'acd_daily', merged],
    queryFn: () => reportsApi.acdDaily(merged),
    refetchInterval: range.preset === 'today' ? 30_000 : false,
    keepPreviousData: true,
  });

  const perQueue = data?.per_queue || [];
  const perDay   = data?.per_day || [];

  return (
    <div>
      <RangeBar value={range} onChange={setRange} />
      <FiltersBar value={filters} onChange={setFilters} show={['queue']} />

      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">{data?.title || 'ACD historical counters'}</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-muted">{data?.subtitle}</span>
          <HelpButton moduleKey="reports" />
          <ExportButtons report="acd/daily" params={merged} disabled={isLoading} />
          <button
            type="button"
            onClick={() => refetch()}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle"
          >↻ {isFetching ? '…' : 'Refresh'}</button>
        </div>
      </div>

      {isError && (
        <div className="text-sm text-red-600 p-3 rounded-thin border border-red-300/40 mb-3">
          {error?.detail || error?.message || 'Failed to load report.'}
        </div>
      )}
      {data?.meta?.source && (
        <div className="text-xs text-surface-muted mb-2">Data source: <strong>{data.meta.source}</strong></div>
      )}
      {!isLoading && (perQueue.length === 0 && perDay.length === 0) && (
        <div className="text-sm text-surface-muted p-3 rounded-thin border border-surface-border dark:border-surface-dark-border">
          No data in this range. Note: <code>acd_daily_stats</code> started collecting on 2026-07-09;
          dates before that will be empty. Also confirm the tenant timezone in <code>clients.timezone</code>
          if today's numbers look off.
        </div>
      )}

      {perDay.length > 0 && (
        <div className="mb-4 rounded-thin border border-surface-border dark:border-surface-dark-border p-3 bg-surface-panel dark:bg-surface-dark-panel">
          <h3 className="text-sm font-semibold mb-2">Daily totals (all queues)</h3>
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={perDay} margin={{ top: 8, right: 20, bottom: 24, left: 0 }}>
                <CartesianGrid stroke="rgba(120,120,120,0.15)" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} angle={-20} dy={10} height={44} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="calls_answered"    fill="#3BD162" name="Answered" />
                <Bar dataKey="calls_abandoned"   fill="#F5A623" name="Abandoned" />
                <Bar dataKey="calls_missed"      fill="#E53935" name="Missed" />
                <Bar dataKey="calls_transferred" fill="#2196F3" name="Transferred" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <ReportTable
        headers={['queue_id','queue_name','offered','calls_answered','calls_abandoned','calls_missed','calls_transferred','calls_hold_events']}
        rows={perQueue}
        columnLabels={{
          queue_id: 'Queue ID', queue_name: 'Queue name',
          offered: 'Offered',
          calls_answered: 'Handled',
          calls_abandoned: 'Abandoned',
          calls_missed: 'Missed',
          calls_transferred: 'Transferred',
          calls_hold_events: 'Hold events',
        }}
      />
    </div>
  );
}
